import { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  FileText,
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  UserCircle,
  Bell,
  CheckCircle,
  AlertCircle,
  FileUp,
  ClipboardList,
  X,
  Shield
} from "lucide-react";

const Layout = ({ children }) => {
  const { user, api, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  const navItems = [
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    { name: "Clients", path: "/clients", icon: UserCircle },
    { name: "Team", path: "/team", icon: Users },
    ...(isAdmin ? [{ name: "Admin", path: "/admin", icon: Shield }] : []),
  ];

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const [notifRes, countRes] = await Promise.all([
          api.get("/notifications?limit=20"),
          api.get("/notifications/unread-count")
        ]);
        setNotifications(notifRes.data);
        setUnreadCount(countRes.data.count);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [api]);

  const handleMarkRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all read:", error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "analysis_complete": return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "ocr_complete": return <FileUp className="w-4 h-4 text-blue-500" />;
      case "needs_review": return <AlertCircle className="w-4 h-4 text-amber-500" />;
      case "document_uploaded": return <FileText className="w-4 h-4 text-slate-500" />;
      default: return <ClipboardList className="w-4 h-4 text-slate-500" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const handleLogout = () => {
    logout();
  };

  const NavLink = ({ item, mobile = false }) => (
    <Button
      variant="ghost"
      className={`
        w-full justify-start font-body rounded-sm
        ${isActive(item.path) 
          ? "bg-slate-100 text-slate-900 font-medium" 
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
        }
        ${mobile ? "h-12" : "h-10"}
      `}
      onClick={() => {
        navigate(item.path);
        if (mobile) setMobileMenuOpen(false);
      }}
      data-testid={`nav-${item.name.toLowerCase()}`}
    >
      <item.icon className="w-4 h-4 mr-3" />
      {item.name}
    </Button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header */}
      <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="h-full px-4 lg:px-8 flex items-center justify-between">
          {/* Logo & Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="sm" className="p-2" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5 text-slate-600" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-heading font-bold text-slate-900">Refund Scope</span>
                  </div>
                </div>
                <nav className="p-4 space-y-1">
                  {navItems.map((item) => (
                    <NavLink key={item.path} item={item} mobile />
                  ))}
                </nav>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/dashboard")}
              data-testid="logo"
            >
              <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-slate-900 hidden sm:block">Refund Scope</span>
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                className={`
                  font-body rounded-sm px-4
                  ${isActive(item.path) 
                    ? "bg-slate-100 text-slate-900 font-medium" 
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }
                `}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.name}
              </Button>
            ))}
          </nav>

          {/* Right Side - Notifications & User */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative p-2 hover:bg-slate-50 rounded-sm"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                  <h3 className="font-heading font-semibold text-slate-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs text-slate-500 hover:text-slate-700 h-auto py-1 px-2"
                      onClick={handleMarkAllRead}
                    >
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="max-h-[350px]">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center">
                      <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {notifications.map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-3 hover:bg-slate-50 cursor-pointer relative group ${!notif.read ? "bg-blue-50/50" : ""}`}
                          onClick={() => {
                            if (!notif.read) handleMarkRead(notif.notification_id);
                            if (notif.client_id) {
                              navigate(`/clients/${notif.client_id}`);
                              setNotifOpen(false);
                            }
                          }}
                        >
                          <div className="flex gap-3">
                            <div className="mt-0.5">{getNotificationIcon(notif.type)}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900">{notif.title}</p>
                              <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                              <p className="text-xs text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5"></div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 w-6 h-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notif.notification_id);
                            }}
                          >
                            <X className="w-3 h-3 text-slate-400" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* User Menu */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2 px-2 hover:bg-slate-50 rounded-sm"
                data-testid="user-menu-btn"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-slate-100 text-slate-600 font-heading font-semibold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="font-body text-sm text-slate-700 hidden sm:block max-w-[120px] truncate">
                  {user?.name}
                </span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <p className="font-body font-medium text-slate-900">{user?.name}</p>
                  {isAdmin && (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-xs">Admin</Badge>
                  )}
                </div>
                <p className="font-body text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              {isAdmin && (
                <>
                  <DropdownMenuItem 
                    onClick={() => navigate("/admin")}
                    className="cursor-pointer"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    <span className="font-body">Admin Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem 
                onClick={handleLogout}
                className="cursor-pointer text-rose-600 focus:text-rose-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                <span className="font-body">Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
